import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VISA_FAQ_CATEGORIES, getVisaCategoryDef } from "@/content/visa-corpus/faq-defs";

export const Route = createFileRoute("/visa/faq/$category")({
  loader: async ({ params }) => {
    const def = getVisaCategoryDef(params.category);
    if (!def) throw notFound();
    const { getVisaFaqsByCategoryTh } = await import("@/content/visa-corpus");
    return { def, faqs: getVisaFaqsByCategoryTh(def.slug) };
  },
  component: VisaFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "หมวดคำถามไม่พบ" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    const title = `${def.titleTh} — FAQ วีซ่าไทย ${faqs.length} ข้อ`;
    return {
      ...pageHeadForProvince({
        title,
        description: def.descTh,
        path: `/visa/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/visa/faq/${params.category}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "วีซ่าและตรวจคนเข้าเมือง FAQ", url: `${site}/visa/faq` },
              { name: def.titleTh, url: `${site}/visa/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/visa/faq/${params.category}`)) },
      ],
    };
  },
});

function VisaFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/visa/faq" className="hover:underline">วีซ่า FAQ</a> <span>/</span>{" "}
          <span>{def.titleTh}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleTh}</h1>
        <p className="text-muted-foreground mb-6">{def.descTh}</p>
        <p className="text-sm text-muted-foreground mb-8">
          รวม <strong>{faqs.length}</strong> คำถามในหมวดนี้ — อ้างอิงประกาศของสำนักงานตรวจคนเข้าเมือง
          กรมการจัดหางาน และกระทรวงการต่างประเทศ ทุกกรณีอยู่ในดุลพินิจของพนักงานเจ้าหน้าที่
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {VISA_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/visa/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleTh}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              ยังไม่มีคำถามในหมวดนี้ — <a href="/contact" className="text-primary underline">สอบถามทีมงาน</a>
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ปรึกษาทีมงานเรื่องวีซ่าและเอกสาร</h2>
          <p className="text-sm text-muted-foreground mb-4">จ.-ส. 9:00-18:00</p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">ติดต่อทีมงาน →</a>
        </section>

        <FaqSourceNote topic="visa" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}